import { create } from 'zustand'
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  onSnapshot
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { useAuthStore } from './auth'
import { useCityStore } from './city'
import { usePaymentStore } from './payment'
import { getEffectiveToday, isMockDateActive } from '@/utils/mockDate'
import { resolveDisplayName } from '@/utils/displayName'
import { cityIdReadSet, normalizeCityId } from '@/utils/cityIds'
import {
  formatClockLabel,
  inferCheckInCloseMode,
  parseDateTimeLocal,
  resolveCheckInCloseAt
} from '@/utils/checkInWindow'
import {
  formatLocalDateString,
  resolveScheduleTimeForDate
} from '@/utils/scheduleDisplay'

/** One-shot payload for Cloud Function attendance attribution (cleared after log write). */
function attendanceMetaPatch (actorUid, targetUid, action) {
  return {
    _attendanceMeta: {
      actorUid,
      targetUid,
      action,
      at: serverTimestamp()
    }
  }
}

/** Auth state, or null if the auth store isn't initialised yet (import cycles). */
const authSnapshot = (): any => {
  try {
    return useAuthStore.getState()
  } catch {
    return null
  }
}

export interface GameSchedule {
  day: any
  time: string
  venue: string
  cityId: string
  pace: number
  dayName: string
  scheduleMode: string
  startDate: string | null
  endDate: string | null
  checkInOpenTime: string | null
  checkInCloseMode: string | null
  checkInCloseTime: string | null
  checkInCloseOffsetMinutes: number | null
  sessionTimes: Record<string, any>
  regularsCapacity: 'full' | 'open'
  [key: string]: any
}

export interface TodayGameSlot {
  gameId: string
  scheduleKey: string
  schedule: GameSchedule
}

export interface CheckInWindow {
  openAt: Date
  closeAt: Date
  openLabel: string
  closeLabel: string
  isOpenNow: boolean
}

export interface TeamBalance {
  darkTeam: { goalie: any; forwards: any[]; defense: any[] }
  lightTeam: { goalie: any; forwards: any[]; defense: any[] }
  overflow: any[]
  conversions: any[]
}

export interface GameState {
  games: any[]
  currentGame: any | null
  currentCityId: string | null
  loading: boolean
  schedulesLoaded: boolean
  gameSchedules: Record<string, GameSchedule>
  scheduleOverrides: any[]
  lastRefreshed: number | null
  todayGames: any[]
  // derived
  /** Legacy alias of `gameSchedules`. */
  GAME_SCHEDULES: Record<string, GameSchedule>
  isDataStale: boolean
  timeSinceRefresh: string | null
  userCheckedIn: boolean
  // actions
  loadSchedules: (cityId?: string | null, forceRefresh?: boolean) => { success: boolean; error?: string }
  subscribeToSchedules: (cityId?: string | null) => { success: boolean; error?: string }
  stopSchedulesListener: () => void
  loadSchedulesForCity: (cityId: string) => Promise<{ success: boolean; schedules?: Record<string, GameSchedule>; error?: string }>
  getSchedulesForCity: (cityId: string) => Record<string, GameSchedule>
  subscribeToOverrides: (cityId?: string | null) => void
  stopOverridesListener: () => void
  isGameCancelledToday: (scheduleId: string) => boolean
  getCancellationReason: (scheduleId: string) => string | null
  subscribeToTodayGame: (cityId?: string | null) => Promise<any | null>
  getTodayGames: (cityId?: string | null) => TodayGameSlot[]
  getTodayGameForUser: (cityId?: string | null) => TodayGameSlot | null
  getTodayGameId: (cityId?: string | null) => string | null
  getTodayGameScheduleKey: (cityId?: string | null) => string | null
  isCheckInAllowed: (gameLike?: any) => boolean
  getCheckInWindowForGame: (gameLike: any) => CheckInWindow
  loadTodayGame: (cityId?: string | null) => Promise<any | null>
  refreshGame: (cityId?: string | null, refreshSchedules?: boolean) => Promise<any | null>
  stopGameListener: () => void
  checkIn: (cityId?: string | null, paymentInfo?: Record<string, any>) => Promise<any>
  checkOut: (cityId?: string | null, gameIdOverride?: string | null) => Promise<any>
  moveFromWaitlist: (playerUid: string, cityId?: string | null) => Promise<{ success: boolean; error?: string }>
  balanceTeams: (players: any[]) => TeamBalance
  setCurrentCity: (cityId: string | null) => void
}

// Real-time listener unsubscribe functions
let schedulesUnsubscribe: (() => void) | null = null
let gameUnsubscribe: (() => void) | null = null
const gameUnsubscribes: Array<() => void> = [] // For multi-game subscription
let overridesUnsubscribe: (() => void) | null = null

// Cache for schedules per city (for multi-city views). Not part of the store's
// public surface in the legacy store either, so it stays module-level.
const schedulesPerCity: Record<string, Record<string, GameSchedule>> = {}

const FALLBACK_CALLABLE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'internal',
  'unknown',
  'not-found',
  'unimplemented'
])

const normalizeCallableCode = (code) => {
  if (!code) return ''
  return String(code).replace('functions/', '').trim()
}

const parseCallableError = (error) => {
  const code = normalizeCallableCode(error?.code)
  const details = error?.details && typeof error.details === 'object'
    ? error.details
    : null
  const updateRequired = details?.updateRequired === true || details?.code === 'APP_UPDATE_REQUIRED'
  return {
    code,
    message: details?.message || error?.message || 'Unexpected error',
    updateRequired,
    updateUrl: details?.updateUrl || null,
    minimumVersion: details?.minimumVersion || null,
    platform: details?.platform || null
  }
}

const buildRequestId = (action, gameId, uid) => (
  `${action}_${gameId || 'none'}_${uid}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
)

const getClientInfo = async () => {
  // Web build: Capacitor.getPlatform() is always 'web', so no native app version.
  const platform = 'web'
  const clientInfo: Record<string, any> = {
    source: 'app',
    platform,
    appVersion: ''
  }

  // Dev-only: simulate native client for update-gate testing on web (see docs/V3_1_UPDATE_GATE_CUTOVER.md).
  if (import.meta.env.DEV && import.meta.env.VITE_SIMULATE_UPDATE_GATE === 'true') {
    clientInfo.platform = import.meta.env.VITE_SIMULATE_UPDATE_PLATFORM || 'ios'
    clientInfo.appVersion = import.meta.env.VITE_SIMULATE_UPDATE_APP_VERSION || '1.0.0'
  }

  return clientInfo
}

const callCheckInFunction = async ({ gameId, cityId, paymentInfo, uid }) => {
  const functions = getFunctions()
  const checkInFn = httpsCallable(functions, 'checkInToGame')
  const clientInfo = await getClientInfo()
  const requestId = buildRequestId('checkin', gameId, uid)
  const result = await checkInFn({
    gameId,
    cityId,
    paymentInfo,
    requestId,
    clientInfo
  })
  return (result?.data || {}) as any
}

const callCheckOutFunction = async ({ gameId, uid }) => {
  const functions = getFunctions()
  const checkOutFn = httpsCallable(functions, 'checkOutFromGame')
  const clientInfo = await getClientInfo()
  const requestId = buildRequestId('checkout', gameId, uid)
  const result = await checkOutFn({
    gameId,
    requestId,
    clientInfo
  })
  return (result?.data || {}) as any
}

const mapScheduleDoc = (data): GameSchedule => ({
  day: data.dayOfWeek,
  time: data.time,
  venue: data.venue,
  cityId: data.cityId,
  pace: data.pace || 2,
  dayName: data.dayName,
  scheduleMode: data.scheduleMode || 'weekly',
  startDate: data.startDate || null,
  endDate: data.endDate || null,
  checkInOpenTime: data.checkInOpenTime || null,
  checkInCloseMode: data.checkInCloseMode || null,
  checkInCloseTime: data.checkInCloseTime || null,
  checkInCloseOffsetMinutes: data.checkInCloseOffsetMinutes ?? null,
  sessionTimes: data.sessionTimes || {},
  regularsCapacity: data.regularsCapacity === 'full' ? 'full' : 'open'
})

export const useGameStore = create<GameState>()(
  derived<GameState>((s) => {
    // Get time since last refresh in a human-readable format
    let timeSinceRefresh: string | null = null
    if (s.lastRefreshed) {
      const seconds = Math.floor((Date.now() - s.lastRefreshed) / 1000)
      if (seconds < 60) {
        timeSinceRefresh = 'just now'
      } else {
        const minutes = Math.floor(seconds / 60)
        if (minutes < 60) {
          timeSinceRefresh = `${minutes}m ago`
        } else {
          const hours = Math.floor(minutes / 60)
          timeSinceRefresh = `${hours}h ago`
        }
      }
    }

    // userCheckedIn reads the auth store; it is kept fresh by the auth
    // subscription at the bottom of this file.
    let userCheckedIn = false
    const authUser = authSnapshot()?.user
    if (authUser) {
      const gamesToCheck = s.todayGames.length > 0 ? s.todayGames : (s.currentGame ? [s.currentGame] : [])
      userCheckedIn = gamesToCheck.some(g => {
        const inPlayers = g.players?.some(p => p.uid === authUser.uid)
        const inWaitlist = g.waitlist?.some(p => p.uid === authUser.uid)
        return inPlayers || inWaitlist
      })
    }

    return {
      GAME_SCHEDULES: s.gameSchedules,
      // With real-time listeners, data is never stale
      isDataStale: false,
      timeSinceRefresh,
      userCheckedIn
    }
  })((set, get) => {
    // Subscribe to schedule overrides for a specific city (real-time)
    const subscribeToOverrides = (cityId?: string | null) => {
      const city = normalizeCityId(cityId || get().currentCityId)
      if (!city) return

      // Unsubscribe from previous listener if exists
      if (overridesUnsubscribe) {
        overridesUnsubscribe()
        overridesUnsubscribe = null
      }

      // Set up real-time listener for overrides. Players only need today and later
      // (yesterday kept as a timezone buffer); past cancellations stay with the admin store.
      // Needs the scheduleOverrides (cityId, date) composite index.
      const overridesRef = collection(db, 'scheduleOverrides')
      const readIds = cityIdReadSet(city)
      const fromDate = getEffectiveToday()
      fromDate.setDate(fromDate.getDate() - 1)
      const q = query(
        overridesRef,
        where('cityId', readIds.length > 1 ? 'in' : '==', readIds.length > 1 ? readIds : city),
        where('date', '>=', formatLocalDateString(fromDate))
      )

      overridesUnsubscribe = onSnapshot(q, (querySnapshot) => {
        // Sort client-side by date
        set({
          scheduleOverrides: querySnapshot.docs
            .map(docSnap => ({
              id: docSnap.id,
              ...docSnap.data()
            }) as any)
            .sort((a, b) => a.date.localeCompare(b.date))
        })
      }, (error) => {
        console.error('Error in overrides listener:', error)
      })
    }

    // Subscribe to schedules for a specific city (real-time)
    const subscribeToSchedules = (cityId?: string | null) => {
      const currentCityId = normalizeCityId(cityId || useCityStore.getState().currentCityId)
      set({ currentCityId })

      if (!currentCityId) {
        console.warn('No city ID provided for schedules')
        set({ schedulesLoaded: true })
        return { success: false, error: 'No city ID' }
      }

      // Unsubscribe from previous listener if exists
      if (schedulesUnsubscribe) {
        schedulesUnsubscribe()
        schedulesUnsubscribe = null
      }

      // Clear old schedules immediately to prevent stale data when switching cities
      set({ gameSchedules: {}, schedulesLoaded: false })

      // Set up real-time listener
      const schedulesRef = collection(db, 'gameSchedules')
      const readIds = cityIdReadSet(currentCityId)
      const q =
        readIds.length > 1
          ? query(schedulesRef, where('cityId', 'in', readIds))
          : query(
            schedulesRef,
            where('cityId', '==', currentCityId),
            orderBy('order', 'asc')
          )

      schedulesUnsubscribe = onSnapshot(q, (querySnapshot) => {
        const schedules: Record<string, GameSchedule> = {}
        querySnapshot.docs
          .sort((a, b) => {
            const oa = Number(a.data().order ?? 9999)
            const ob = Number(b.data().order ?? 9999)
            return oa - ob
          })
          .forEach(docSnap => {
            const data = docSnap.data()
            if (data.isActive) {
              schedules[docSnap.id] = mapScheduleDoc(data)
            }
          })

        set({ gameSchedules: schedules, schedulesLoaded: true })
      }, (error) => {
        console.error('Error in schedules listener:', error)
        set({ schedulesLoaded: true })
      })

      // Also subscribe to overrides for this city
      subscribeToOverrides(currentCityId)

      return { success: true }
    }

    // Check if today's game is cancelled for a specific schedule
    const isGameCancelledToday = (scheduleId: string) => {
      const today = formatLocalDateString(getEffectiveToday())
      return get().scheduleOverrides.some(
        override => override.scheduleId === scheduleId && override.date === today
      )
    }

    // Get the cancellation reason if game is cancelled today
    const getCancellationReason = (scheduleId: string) => {
      const today = formatLocalDateString(getEffectiveToday())
      const override = get().scheduleOverrides.find(
        o => o.scheduleId === scheduleId && o.date === today
      )
      return override?.reason || null
    }

    // Get all today's game slots for a city (for same-day multi-venue)
    // Returns array of { gameId, scheduleKey, schedule } for schedules matching today, not cancelled
    const getTodayGames = (cityId?: string | null): TodayGameSlot[] => {
      const city = normalizeCityId(cityId || get().currentCityId)
      if (!city) return []
      const cityReadIds = new Set(cityIdReadSet(city))

      const now = getEffectiveToday()
      const dateString = formatLocalDateString(now)

      const schedules = get().gameSchedules
      const result: TodayGameSlot[] = []
      for (const scheduleKey of Object.keys(schedules)) {
        const schedule = schedules[scheduleKey]
        if (schedule == null) continue
        if (!cityReadIds.has(schedule.cityId)) continue

        const resolvedTime = resolveScheduleTimeForDate(schedule, dateString)
        if (!resolvedTime) continue
        if (isGameCancelledToday(scheduleKey)) continue
        result.push({
          gameId: `${dateString}_${scheduleKey}`,
          scheduleKey,
          schedule: {
            ...schedule,
            time: resolvedTime
          }
        })
      }
      return result
    }

    // Auto-select which game to show/check-in to when multiple exist
    // Returns { gameId, scheduleKey, schedule } or null
    const getTodayGameForUser = (cityId?: string | null) => {
      const games = getTodayGames(cityId)
      if (games.length === 0) return null
      if (games.length === 1) return games[0]
      const authStore = useAuthStore.getState()
      const regularGame = games.find(g =>
        authStore.isRegularForSchedule(g.scheduleKey, cityId || get().currentCityId)
      )
      return regularGame || games[0]
    }

    // Get today's game ID for the current city (backward compatible)
    // When multiple games exist, returns the auto-selected one
    const getTodayGameId = (cityId?: string | null) => {
      const selected = getTodayGameForUser(cityId)
      return selected ? selected.gameId : null
    }

    const getTodayGameScheduleKey = (cityId?: string | null) => {
      const selected = getTodayGameForUser(cityId)
      return selected ? selected.scheduleKey : null
    }

    const getCheckInWindowForGame = (gameLike: any): CheckInWindow => {
      const now = getEffectiveToday()
      const fallbackDate = formatLocalDateString(now)
      const schedule = gameLike?.scheduleKey ? get().gameSchedules[gameLike.scheduleKey] : null
      // Schedule is source of truth when linked (so admin edits apply to existing skates).
      const cfg = schedule ? { ...gameLike, ...schedule } : gameLike

      // Prefer YYYY-MM-DD from games/{date}_{scheduleKey} over a stale/mismatched date field.
      const idDateMatch = String(gameLike?.id || gameLike?.gameId || '').match(/^(\d{4}-\d{2}-\d{2})_/)
      const dateStr = String(idDateMatch?.[1] || cfg?.date || fallbackDate).split('T')[0]
      const startTime = cfg?.time || schedule?.time || null
      const openTime = cfg?.checkInOpenTime || schedule?.checkInOpenTime || '08:00'

      const openAt = (parseDateTimeLocal(dateStr, openTime) || parseDateTimeLocal(dateStr, '08:00')) as Date
      const closeAt = (resolveCheckInCloseAt({
        dateStr,
        startTime,
        checkInCloseMode: cfg?.checkInCloseMode ?? schedule?.checkInCloseMode,
        checkInCloseTime: cfg?.checkInCloseTime ?? schedule?.checkInCloseTime,
        checkInCloseOffsetMinutes:
          cfg?.checkInCloseOffsetMinutes ?? schedule?.checkInCloseOffsetMinutes
      }) || parseDateTimeLocal(dateStr, '18:00')) as Date

      const openLabel = formatClockLabel(openAt.getHours(), openAt.getMinutes())
      const closeLabel = formatClockLabel(closeAt.getHours(), closeAt.getMinutes())
      const isOpenNow = now >= openAt && now < closeAt

      return {
        openAt,
        closeAt,
        openLabel,
        closeLabel,
        isOpenNow
      }
    }

    const isCheckInAllowed = (gameLike: any = null) => {
      if (isMockDateActive()) return true
      if (gameLike) {
        return getCheckInWindowForGame(gameLike).isOpenNow
      }
      const { todayGames, currentGame } = get()
      if (todayGames.length > 0) {
        return todayGames.some(g => getCheckInWindowForGame(g).isOpenNow)
      }
      if (currentGame) {
        return getCheckInWindowForGame(currentGame).isOpenNow
      }
      const hours = getEffectiveToday().getHours()
      return hours >= 8 && hours < 18
    }

    // Subscribe to all today's games (real-time) - supports same-day multi-venue
    const subscribeToTodayGame = async (cityId?: string | null) => {
      const city = cityId || get().currentCityId
      const gameSlots = getTodayGames(city)

      if (gameSlots.length === 0) {
        set({ todayGames: [], currentGame: null })
        return null
      }

      // Unsubscribe from previous game listeners
      for (const unsub of gameUnsubscribes) {
        if (typeof unsub === 'function') unsub()
      }
      gameUnsubscribes.length = 0

      set({ loading: true })
      // Must match getTodayGames() / gameId prefix — never use UTC via toISOString().
      const dateStr = formatLocalDateString(getEffectiveToday())

      const updateCurrentGame = () => {
        const authStore = useAuthStore.getState()
        const userUid = authStore.user?.uid
        const { todayGames } = get()
        const gameUserIsIn = userUid && todayGames.find(g =>
          g.players?.some(p => p.uid === userUid) || g.waitlist?.some(p => p.uid === userUid)
        )
        if (gameUserIsIn) {
          set({ currentGame: gameUserIsIn })
        } else {
          const selected = getTodayGameForUser(city)
          const selectedGame = todayGames.find(g =>
            selected && g.id === selected.gameId
          )
          set({ currentGame: selectedGame || todayGames[0] || null })
        }
      }

      try {
        for (const { gameId, scheduleKey, schedule } of gameSlots) {
          const docRef = doc(db, 'games', gameId)
          const docSnap = await getDoc(docRef)

          if (!docSnap.exists()) {
            const newGame = {
              date: dateStr,
              scheduleKey,
              cityId: city,
              venue: schedule.venue,
              time: schedule.time,
              pace: schedule.pace || 2,
              checkInOpenTime: schedule.checkInOpenTime || '08:00',
              checkInCloseMode: schedule.checkInCloseMode || inferCheckInCloseMode(schedule),
              checkInCloseTime: schedule.checkInCloseTime || null,
              checkInCloseOffsetMinutes: schedule.checkInCloseOffsetMinutes ?? null,
              players: [],
              waitlist: [],
              createdAt: new Date().toISOString()
            }
            await setDoc(docRef, newGame)
          } else if (schedule.time && docSnap.data().time !== schedule.time) {
            await updateDoc(docRef, {
              time: schedule.time,
              updatedAt: new Date().toISOString()
            })
          }

          const unsub = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
              const data = { id: snap.id, ...snap.data() }
              const current = get().todayGames
              const idx = current.findIndex(g => g.id === snap.id)
              const next = [...current]
              if (idx >= 0) {
                next[idx] = data
              } else {
                next.push(data)
              }
              set({ todayGames: next, lastRefreshed: Date.now() })
              updateCurrentGame()
            }
            set({ loading: false })
          }, (error) => {
            console.error('Error in game listener:', error)
            set({ loading: false })
          })

          gameUnsubscribes.push(unsub)
        }

        return get().currentGame
      } catch (error) {
        console.error('Error setting up game listeners:', error)
        set({ loading: false, todayGames: [], currentGame: null })
        return null
      }
    }

    const legacyCheckIn = async (cityId, paymentInfo: Record<string, any> = {}) => {
      const authStore = useAuthStore.getState()
      if (!authStore.user || !authStore.userProfile) {
        return { success: false, error: 'Must be logged in' }
      }

      const city = cityId || get().currentCityId
      const targetGameId = paymentInfo.gameId || getTodayGameId(city)
      if (!targetGameId) {
        return { success: false, error: 'No game scheduled for today' }
      }

      const gameSlots = getTodayGames(city)
      const targetSlot = gameSlots.find(g => g.gameId === targetGameId)
      if (!targetSlot) {
        return { success: false, error: 'Invalid game selection' }
      }

      if (isGameCancelledToday(targetSlot.scheduleKey)) {
        const reason = getCancellationReason(targetSlot.scheduleKey)
        const message = reason
          ? `Today's game has been cancelled: ${reason}`
          : "Today's game has been cancelled"
        return { success: false, error: message }
      }

      const targetGameData = get().todayGames.find(g => g.id === targetGameId) || {
        id: targetGameId,
        date: formatLocalDateString(getEffectiveToday()),
        scheduleKey: targetSlot.scheduleKey,
        time: targetSlot.schedule?.time,
        checkInOpenTime: targetSlot.schedule?.checkInOpenTime,
        checkInCloseMode: targetSlot.schedule?.checkInCloseMode,
        checkInCloseTime: targetSlot.schedule?.checkInCloseTime,
        checkInCloseOffsetMinutes: targetSlot.schedule?.checkInCloseOffsetMinutes
      }
      if (!isCheckInAllowed(targetGameData)) {
        const window = getCheckInWindowForGame(targetGameData)
        return {
          success: false,
          error: `Check-in is closed. Window: ${window.openLabel} to ${window.closeLabel}.`
        }
      }

      // Already checked in somewhere today? Two different answers depending on
      // WHERE, and the distinction matters because this path runs as a fallback
      // after a callable that may already have committed its write.
      //
      // Previously this compared `gameDoc.data().id` against the target id. A
      // Firestore document's id is not part of its data — these documents have
      // no `id` field, the store injects it on read — so that comparison was
      // `undefined !== targetGameId`, i.e. always true. Any game the player was
      // found in, INCLUDING the one they were checking into, produced
      // "You are already checked in for a game today."
      //
      // That is the reported bug: a successful check-in followed by an error.
      // The callable throws a retryable code (deadline-exceeded / internal are
      // routine on a cold start) *after* committing, the client falls back to
      // here, finds the player on the target roster the callable just wrote,
      // and reports failure for a check-in that actually worked.
      const todayDate = formatLocalDateString(getEffectiveToday())
      try {
        const gamesRef = collection(db, 'games')
        const todayGamesQuery = query(gamesRef, where('date', '==', todayDate))
        const todayGamesSnapshot = await getDocs(todayGamesQuery)

        for (const gameDoc of todayGamesSnapshot.docs) {
          const gameData = gameDoc.data()
          const inPlayers = gameData.players?.some(p => p.uid === authStore.user!.uid)
          const inWaitlist = gameData.waitlist?.some(p => p.uid === authStore.user!.uid)
          if (!inPlayers && !inWaitlist) continue

          // Already on the roster for the game being checked into: this is a
          // repeat of work that already succeeded, so report success. Mirrors
          // the callable, which returns { success: true, alreadyCheckedIn: true }
          // for the same case rather than erroring.
          if (gameDoc.id === targetGameId) {
            return {
              success: true,
              alreadyCheckedIn: true,
              list: inPlayers ? 'players' : 'waitlist',
              isRegular: inPlayers === true
            }
          }

          // Checked into a different venue tonight — a genuine conflict.
          return {
            success: false,
            error: 'You are already checked in for a game today. Please check out first to switch venues.'
          }
        }
      } catch (error) {
        console.error('Error checking for existing check-ins:', error)
        // Continue with check-in if we can't verify (fail open for better UX)
      }

      const scheduleKey = targetSlot.scheduleKey

      // Check if user is a regular for this schedule (using city-scoped data)
      const isRegular = authStore.isRegularForSchedule(scheduleKey, city)

      try {
        const docRef = doc(db, 'games', targetGameId)

        // Build player data with fallbacks for all fields
        // Firestore arrayUnion doesn't accept undefined or null values
        // Use provided position (from check-in modal) or fall back to profile position
        const position = paymentInfo.position || authStore.userProfile.position || 'Forward'
        const isGoalie = position === 'Goalie' || paymentInfo.isGoalie === true

        const displayName = resolveDisplayName({
          name: authStore.userProfile?.name,
          email: authStore.userProfile?.email || authStore.user?.email,
          displayName: authStore.user?.displayName
        })
        if (!displayName) {
          return {
            success: false,
            error: 'Please add your name in Profile before checking in.'
          }
        }

        const playerData: Record<string, any> = {
          uid: authStore.user!.uid,
          name: displayName,
          position: position,
          skillLevel: authStore.userProfile.skillLevel || 2,
          checkedInAt: new Date().toISOString(),
          hasPass: isGoalie ? false : (paymentInfo.hasPass === true), // Goalies don't use passes
          isGoalie: isGoalie // Flag for free goalie check-in
        }

        // Only add paymentMethod if it's defined and not a goalie (Firestore arrayUnion doesn't accept null/undefined)
        if (paymentInfo.paymentMethod && !isGoalie) {
          playerData.paymentMethod = paymentInfo.paymentMethod
        }

        // Position limits
        const MAX_GOALIES = 2
        const MAX_FORWARDS = 12
        const MAX_DEFENSE = 8

        // Check if position slots are available - use target game's roster
        const { todayGames, currentGame } = get()
        const checkInGameData = todayGames.find(g => g.id === targetGameId) ||
          (currentGame?.id === targetGameId ? currentGame : null)
        const players = checkInGameData?.players || []
        let positionSlotsAvailable = true

        if (isGoalie) {
          const currentGoalies = players.filter(p => p.position === 'Goalie').length
          positionSlotsAvailable = currentGoalies < MAX_GOALIES
        } else if (position === 'Forward') {
          const currentForwards = players.filter(p => p.position === 'Forward').length
          positionSlotsAvailable = currentForwards < MAX_FORWARDS
        } else if (position === 'Defense') {
          const currentDefense = players.filter(p => p.position === 'Defense').length
          positionSlotsAvailable = currentDefense < MAX_DEFENSE
        }

        const uid = authStore.user!.uid
        const onRoster = positionSlotsAvailable && isRegular
        if (onRoster) {
          await updateDoc(docRef, {
            players: arrayUnion(playerData),
            ...attendanceMetaPatch(uid, uid, 'self_checkin_roster')
          })
        } else {
          await updateDoc(docRef, {
            waitlist: arrayUnion(playerData),
            ...attendanceMetaPatch(uid, uid, 'self_checkin_waitlist')
          })
        }

        const cityStore = useCityStore.getState()
        cityStore.rememberPickupCheckInCity(city)
        try {
          await updateDoc(doc(db, 'users', uid), {
            lastPickupCheckInCityId: city,
            lastPickupCheckInGameId: targetGameId,
            lastPickupCheckInAt: serverTimestamp()
          })
        } catch (error) {
          console.warn('Unable to save last pickup check-in city:', error)
        }

        // No need to manually refresh - real-time listener will update automatically
        return {
          success: true,
          isRegular: onRoster,
          list: onRoster ? 'players' : 'waitlist'
        }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }

    const legacyCheckOut = async (cityId, gameIdOverride: string | null = null) => {
      const authStore = useAuthStore.getState()
      const paymentStore = usePaymentStore.getState()

      if (!authStore.user) {
        return { success: false, error: 'Must be logged in' }
      }

      const city = cityId || get().currentCityId
      let gameId = gameIdOverride
      let game = get().currentGame

      if (!gameId) {
        game = get().todayGames.find(g =>
          g.players?.some(p => p.uid === authStore.user!.uid) ||
          g.waitlist?.some(p => p.uid === authStore.user!.uid)
        )
        gameId = game?.id || getTodayGameId(city)
      } else {
        game = get().todayGames.find(g => g.id === gameId) || get().currentGame
      }

      if (!gameId || !game) {
        return { success: false, error: 'No game scheduled for today' }
      }

      try {
        const docRef = doc(db, 'games', gameId)

        const playerInGame = game.players?.find(p => p.uid === authStore.user!.uid)
        const playerInWaitlist = game.waitlist?.find(p => p.uid === authStore.user!.uid)
        const player = playerInGame || playerInWaitlist

        if (!player) {
          return { success: false, error: 'You are not checked in to this game' }
        }

        // Cancel authorized payment if exists (credit card payment that hasn't been captured)
        if (player?.paymentIntentId && player?.paymentStatus === 'authorized') {
          try {
            await paymentStore.cancelPaymentAuthorization(player.paymentIntentId)
          } catch (err) {
            console.error('Error canceling payment authorization:', err)
            // Continue with checkout even if cancellation fails
          }
        }

        const selfUid = authStore.user!.uid
        const checkoutMeta = playerInGame
          ? attendanceMetaPatch(selfUid, selfUid, 'self_checkout_roster')
          : attendanceMetaPatch(selfUid, selfUid, 'self_checkout_waitlist')

        const checkoutUpdates: Record<string, any> = playerInGame
          ? { players: arrayRemove(playerInGame), ...checkoutMeta }
          : { waitlist: arrayRemove(playerInWaitlist), ...checkoutMeta }

        if (game?.teamAssignments) {
          const teamAssignments = { ...game.teamAssignments }
          let updated = false

          if (teamAssignments.dark?.some(p => p.uid === authStore.user!.uid)) {
            teamAssignments.dark = teamAssignments.dark.filter(p => p.uid !== authStore.user!.uid)
            updated = true
          }
          if (teamAssignments.light?.some(p => p.uid === authStore.user!.uid)) {
            teamAssignments.light = teamAssignments.light.filter(p => p.uid !== authStore.user!.uid)
            updated = true
          }

          if (updated) {
            checkoutUpdates.teamAssignments = teamAssignments
          }
        }

        await updateDoc(docRef, checkoutUpdates)

        // Remove any pending payment for this game date
        if (game.date) {
          await authStore.removePendingPaymentByGameDate(game.date)
        }

        // No need to manually refresh - real-time listener will update automatically
        return { success: true }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }

    return {
      games: [],
      currentGame: null,
      currentCityId: null,
      loading: false,
      schedulesLoaded: false,
      lastRefreshed: null,
      // All today's games (for same-day multi-venue support)
      todayGames: [],
      // Dynamic schedules loaded from Firestore (city-filtered)
      gameSchedules: {},
      // Schedule overrides (cancelled games)
      scheduleOverrides: [],
      GAME_SCHEDULES: {},
      isDataStale: false,
      timeSinceRefresh: null,
      userCheckedIn: false,

      subscribeToSchedules,

      // Alias for backward compatibility
      loadSchedules: (cityId, forceRefresh = false) => {
        return subscribeToSchedules(cityId)
      },

      // Load schedules for a specific city (one-time fetch, no real-time listener)
      // Used for multi-city views like EditUserPage
      loadSchedulesForCity: async (cityId) => {
        if (!cityId) {
          return { success: false, error: 'No city ID provided' }
        }

        try {
          const schedulesRef = collection(db, 'gameSchedules')
          const canonical = normalizeCityId(cityId)
          const readIds = cityIdReadSet(canonical)
          const q =
            readIds.length > 1
              ? query(schedulesRef, where('cityId', 'in', readIds))
              : query(
                schedulesRef,
                where('cityId', '==', canonical),
                orderBy('order', 'asc')
              )

          const querySnapshot = await getDocs(q)
          const schedules: Record<string, GameSchedule> = {}

          querySnapshot.docs
            .sort((a, b) => {
              const oa = Number(a.data().order ?? 9999)
              const ob = Number(b.data().order ?? 9999)
              return oa - ob
            })
            .forEach(docSnap => {
              const data = docSnap.data()
              if (data.isActive) {
                schedules[docSnap.id] = mapScheduleDoc(data)
              }
            })

          // Cache the schedules for this city
          schedulesPerCity[canonical] = schedules

          return { success: true, schedules }
        } catch (error) {
          console.error(`Error loading schedules for city ${cityId}:`, error)
          return { success: false, error: error.message }
        }
      },

      // Get cached schedules for a specific city
      getSchedulesForCity: (cityId) => {
        return schedulesPerCity[normalizeCityId(cityId)] || {}
      },

      stopSchedulesListener: () => {
        if (schedulesUnsubscribe) {
          schedulesUnsubscribe()
          schedulesUnsubscribe = null
        }
        set({ schedulesLoaded: false, gameSchedules: {} })
      },

      subscribeToOverrides,

      stopOverridesListener: () => {
        if (overridesUnsubscribe) {
          overridesUnsubscribe()
          overridesUnsubscribe = null
        }
        set({ scheduleOverrides: [] })
      },

      isGameCancelledToday,
      getCancellationReason,
      subscribeToTodayGame,
      getTodayGames,
      getTodayGameForUser,
      getTodayGameId,
      getTodayGameScheduleKey,
      isCheckInAllowed,
      getCheckInWindowForGame,

      // Alias for backward compatibility
      loadTodayGame: (cityId) => {
        return subscribeToTodayGame(cityId)
      },

      // Refresh game data (for pull-to-refresh) - with real-time listeners this just re-subscribes
      refreshGame: async (cityId, refreshSchedules = false) => {
        const city = cityId || get().currentCityId

        // Re-subscribe to schedules if requested
        if (refreshSchedules) {
          subscribeToSchedules(city)
        }

        return subscribeToTodayGame(city)
      },

      stopGameListener: () => {
        for (const unsub of gameUnsubscribes) {
          if (typeof unsub === 'function') unsub()
        }
        gameUnsubscribes.length = 0
        if (gameUnsubscribe) {
          gameUnsubscribe()
          gameUnsubscribe = null
        }
        set({ todayGames: [], currentGame: null, lastRefreshed: null })
      },

      checkIn: async (cityId, paymentInfo: Record<string, any> = {}) => {
        const authStore = useAuthStore.getState()
        if (!authStore.user || !authStore.userProfile) {
          return { success: false, error: 'Must be logged in' }
        }

        const city = cityId || get().currentCityId
        const targetGameId = paymentInfo.gameId || getTodayGameId(city)
        if (!targetGameId) {
          return { success: false, error: 'No game scheduled for today' }
        }

        const gameSlots = getTodayGames(city)
        const targetSlot = gameSlots.find(g => g.gameId === targetGameId)
        if (!targetSlot) {
          return { success: false, error: 'Invalid game selection' }
        }

        if (isGameCancelledToday(targetSlot.scheduleKey)) {
          const reason = getCancellationReason(targetSlot.scheduleKey)
          const message = reason
            ? `Today's game has been cancelled: ${reason}`
            : "Today's game has been cancelled"
          return { success: false, error: message }
        }

        try {
          const callableResult = await callCheckInFunction({
            gameId: targetGameId,
            cityId: city,
            paymentInfo,
            uid: authStore.user!.uid
          })

          const cityStore = useCityStore.getState()
          cityStore.rememberPickupCheckInCity(city)
          try {
            await updateDoc(doc(db, 'users', authStore.user!.uid), {
              lastPickupCheckInCityId: city,
              lastPickupCheckInGameId: targetGameId,
              lastPickupCheckInAt: serverTimestamp()
            })
          } catch (error) {
            console.warn('Unable to save last pickup check-in city:', error)
          }

          return {
            success: true,
            isRegular: callableResult?.isRegular === true,
            list: callableResult?.list || null,
            alreadyCheckedIn: callableResult?.alreadyCheckedIn === true
          }
        } catch (error) {
          const parsed = parseCallableError(error)
          if (parsed.updateRequired) {
            return {
              success: false,
              error: parsed.message,
              updateRequired: true,
              updateUrl: parsed.updateUrl,
              minimumVersion: parsed.minimumVersion,
              platform: parsed.platform
            }
          }

          if (FALLBACK_CALLABLE_CODES.has(parsed.code)) {
            console.warn('checkIn callable unavailable, using compatibility fallback', parsed)
            return legacyCheckIn(city, paymentInfo)
          }

          return {
            success: false,
            error: parsed.message || 'Failed to check in'
          }
        }
      },

      checkOut: async (cityId, gameIdOverride: string | null = null) => {
        const authStore = useAuthStore.getState()
        const paymentStore = usePaymentStore.getState()
        if (!authStore.user) {
          return { success: false, error: 'Must be logged in' }
        }

        const city = cityId || get().currentCityId
        let gameId = gameIdOverride
        let game = get().currentGame
        if (!gameId) {
          game = get().todayGames.find(g =>
            g.players?.some(p => p.uid === authStore.user!.uid) ||
            g.waitlist?.some(p => p.uid === authStore.user!.uid)
          )
          gameId = game?.id || getTodayGameId(city)
        } else {
          game = get().todayGames.find(g => g.id === gameId) || get().currentGame
        }

        if (!gameId || !game) {
          return { success: false, error: 'No game scheduled for today' }
        }

        const playerInGame = game.players?.find(p => p.uid === authStore.user!.uid)
        const playerInWaitlist = game.waitlist?.find(p => p.uid === authStore.user!.uid)
        const player = playerInGame || playerInWaitlist
        if (!player) {
          return { success: false, error: 'You are not checked in to this game' }
        }

        if (player?.paymentIntentId && player?.paymentStatus === 'authorized') {
          try {
            await paymentStore.cancelPaymentAuthorization(player.paymentIntentId)
          } catch (err) {
            console.error('Error canceling payment authorization:', err)
          }
        }

        try {
          await callCheckOutFunction({
            gameId,
            uid: authStore.user!.uid
          })

          if (game.date) {
            await authStore.removePendingPaymentByGameDate(game.date)
          }
          return { success: true }
        } catch (error) {
          const parsed = parseCallableError(error)
          if (parsed.updateRequired) {
            return {
              success: false,
              error: parsed.message,
              updateRequired: true,
              updateUrl: parsed.updateUrl,
              minimumVersion: parsed.minimumVersion,
              platform: parsed.platform
            }
          }
          if (FALLBACK_CALLABLE_CODES.has(parsed.code)) {
            console.warn('checkOut callable unavailable, using compatibility fallback', parsed)
            return legacyCheckOut(city, gameId)
          }
          return {
            success: false,
            error: parsed.message || 'Failed to check out'
          }
        }
      },

      moveFromWaitlist: async (playerUid, cityId) => {
        const authStore = useAuthStore.getState()
        if (!authStore.user) {
          return { success: false, error: 'Must be logged in' }
        }
        const city = cityId || get().currentCityId
        const gameId = getTodayGameId(city)
        if (!gameId) return { success: false, error: 'No game scheduled' }

        try {
          const docRef = doc(db, 'games', gameId)
          const game = get().currentGame

          const player = game.waitlist?.find(p => p.uid === playerUid)
          if (!player) {
            return { success: false, error: 'Player not found in waitlist' }
          }

          const actorUid = authStore.user!.uid
          await updateDoc(docRef, {
            waitlist: arrayRemove(player),
            players: arrayUnion(player),
            ...attendanceMetaPatch(actorUid, playerUid, 'promote_waitlist_to_roster')
          })

          // No need to manually refresh - real-time listener will update automatically
          return { success: true }
        } catch (error) {
          return { success: false, error: error.message }
        }
      },

      balanceTeams: (players) => {
        // Position limits per team and total
        const MAX_GOALIES = 2      // 1 per team
        const MAX_FORWARDS = 12    // 6 per team
        const MAX_DEFENSE = 8      // 4 per team
        const FORWARDS_PER_TEAM = 6
        const DEFENSE_PER_TEAM = 4

        if (!players || players.length === 0) {
          return {
            darkTeam: { goalie: null, forwards: [], defense: [] },
            lightTeam: { goalie: null, forwards: [], defense: [] },
            overflow: [],
            conversions: []
          }
        }

        const goalies = players.filter(p => p.position === 'Goalie')
        const forwards = players.filter(p => p.position === 'Forward')
        const defense = players.filter(p => p.position === 'Defense')
        const overflow: any[] = []
        const conversions: any[] = []

        const sortBySkill = (a, b) => (b.skillLevel || 2) - (a.skillLevel || 2)

        goalies.sort(sortBySkill)
        forwards.sort(sortBySkill)
        defense.sort(sortBySkill)

        // Step 1: Handle excess goalies - move to overflow (goalies are never converted)
        if (goalies.length > MAX_GOALIES) {
          const excessGoalies = goalies.splice(MAX_GOALIES)
          overflow.push(...excessGoalies)
        }

        // Step 2: Convert excess forwards to defense if defense is short
        if (forwards.length > MAX_FORWARDS && defense.length < MAX_DEFENSE) {
          const excessForwards = forwards.length - MAX_FORWARDS
          const defenseNeeded = MAX_DEFENSE - defense.length
          const toConvert = Math.min(excessForwards, defenseNeeded)

          // Take lowest-skill excess forwards to convert
          const convertedPlayers = forwards.splice(MAX_FORWARDS, toConvert)
          for (const player of convertedPlayers) {
            defense.push({ ...player, position: 'Defense', originalPosition: 'Forward' })
            conversions.push({ player, from: 'Forward', to: 'Defense' })
          }
          defense.sort(sortBySkill)
        }

        // Step 3: Convert excess defense to forwards if forwards is short
        if (defense.length > MAX_DEFENSE && forwards.length < MAX_FORWARDS) {
          const excessDefense = defense.length - MAX_DEFENSE
          const forwardsNeeded = MAX_FORWARDS - forwards.length
          const toConvert = Math.min(excessDefense, forwardsNeeded)

          const convertedPlayers = defense.splice(MAX_DEFENSE, toConvert)
          for (const player of convertedPlayers) {
            forwards.push({ ...player, position: 'Forward', originalPosition: 'Defense' })
            conversions.push({ player, from: 'Defense', to: 'Forward' })
          }
          forwards.sort(sortBySkill)
        }

        // Step 4: Cap positions and move remaining excess to overflow
        if (forwards.length > MAX_FORWARDS) {
          const excessForwards = forwards.splice(MAX_FORWARDS)
          overflow.push(...excessForwards)
        }

        if (defense.length > MAX_DEFENSE) {
          const excessDefense = defense.splice(MAX_DEFENSE)
          overflow.push(...excessDefense)
        }

        // Step 5: Build teams with skill balancing
        const darkTeam = { goalie: null as any, forwards: [] as any[], defense: [] as any[] }
        const lightTeam = { goalie: null as any, forwards: [] as any[], defense: [] as any[] }

        if (goalies.length > 0) darkTeam.goalie = goalies[0]
        if (goalies.length > 1) lightTeam.goalie = goalies[1]

        let darkSkill = darkTeam.goalie?.skillLevel || 0
        let lightSkill = lightTeam.goalie?.skillLevel || 0

        // Distribute forwards while respecting per-team limits
        forwards.forEach((player) => {
          if (darkTeam.forwards.length >= FORWARDS_PER_TEAM) {
            lightTeam.forwards.push(player)
            lightSkill += player.skillLevel || 2
          } else if (lightTeam.forwards.length >= FORWARDS_PER_TEAM) {
            darkTeam.forwards.push(player)
            darkSkill += player.skillLevel || 2
          } else if (darkSkill <= lightSkill) {
            darkTeam.forwards.push(player)
            darkSkill += player.skillLevel || 2
          } else {
            lightTeam.forwards.push(player)
            lightSkill += player.skillLevel || 2
          }
        })

        // Distribute defense while respecting per-team limits
        defense.forEach((player) => {
          if (darkTeam.defense.length >= DEFENSE_PER_TEAM) {
            lightTeam.defense.push(player)
            lightSkill += player.skillLevel || 2
          } else if (lightTeam.defense.length >= DEFENSE_PER_TEAM) {
            darkTeam.defense.push(player)
            darkSkill += player.skillLevel || 2
          } else if (darkSkill <= lightSkill) {
            darkTeam.defense.push(player)
            darkSkill += player.skillLevel || 2
          } else {
            lightTeam.defense.push(player)
            lightSkill += player.skillLevel || 2
          }
        })

        return { darkTeam, lightTeam, overflow, conversions }
      },

      // Set current city (for use when city changes)
      setCurrentCity: (cityId) => {
        set({ currentCityId: normalizeCityId(cityId) })
      }
    }
  })
)

// `userCheckedIn` depends on the signed-in user: recompute derived fields when
// the auth user changes. Deferred if the auth store isn't initialised yet.
const linkAuthStore = () => {
  useAuthStore.subscribe((s, prev) => {
    if (s.user !== prev.user) useGameStore.setState({})
  })
}
try {
  linkAuthStore()
} catch {
  queueMicrotask(linkAuthStore)
}
